import { Box, Button, Text } from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import { useReportingI18n } from '../i18n'

const COOLDOWN_MS = 5000

const DownloadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

interface ExportCsvButtonProps {
  onClick?: () => void
  disabled?: boolean
  label?: string
}

const ExportCsvButton = ({
  onClick,
  disabled = false,
  label,
}: ExportCsvButtonProps) => {
  const { text } = useReportingI18n()
  const [cooldown, setCooldown] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleClick = useCallback(() => {
    if (cooldown) return
    onClick?.()
    setCooldown(true)
    timerRef.current = setTimeout(() => setCooldown(false), COOLDOWN_MS)
  }, [cooldown, onClick])

  const isDisabled = disabled || cooldown

  return (
    <Button
      ml="auto"
      alignSelf="center"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={text('dashboards.csvExport.buttonLabel', 'Export CSV')}
      display="flex"
      height="40px"
      minWidth="40px"
      px="16px"
      py="2px"
      justifyContent="center"
      alignItems="center"
      gap="8px"
      borderRadius="4px"
      bg="brand.solid"
      color="brand.contrast"
      _hover={{ bg: 'brand.solid' }}
      _active={{ bg: 'brand.solid' }}
      _disabled={{
        bg: 'brand.solid',
        color: 'brand.contrast',
        opacity: 0.5,
      }}
    >
      <Box
        as={DownloadIcon}
        width="16px"
        height="16px"
        color="brand.contrast"
      />
      <Text {...textStyles[textStyleKeys.Body3_Bold]} color="brand.contrast">
        {cooldown
          ? text('dashboards.csvExport.exported', 'Exported')
          : (label ?? text('dashboards.csvExport.buttonLabel', 'Export CSV'))}
      </Text>
    </Button>
  )
}

export default ExportCsvButton
